'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { authClient } from '@bip/api-client';
import { LoginModal } from './LoginModal';

interface NavbarLinksProps {
  /**
   * `desktop` (default) — inline list items inside the top-of-header `<ul>`.
   * `mobile` — full-width stacked items rendered inside the collapsible mobile drawer.
   */
  variant?: 'desktop' | 'mobile';
}

/**
 * Sports / Concerts / Shows / Hotels / Restaurants / My Bookings. Rendered
 * twice by `Navbar` (desktop <ul> + mobile drawer).
 *
 * Styling follows sandbox (`nav-link` / `nav-main` / `active-nav`, `text-lg`, mobile `text-right`).
 * ag-local's hotels-route logic is kept on top: route-aware active state (so the current section
 * highlights), the Hotels link points at `/hotels` (the actual route in this app), `<Link>` client
 * navigation, and the My-Bookings login gate.
 */
export function NavbarLinks({ variant = 'desktop' }: NavbarLinksProps) {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const pathname = usePathname();
  //const isRestaurants = pathname?.startsWith('/restaurants') ?? false;

  useEffect(() => {
    authClient
      .getMe()
      .then((res) => setIsLoggedIn(res.message === 'success'))
      .catch(() => setIsLoggedIn(false));
  }, []);

  const handleMyBookings = (e: React.MouseEvent) => {
    if (isLoggedIn) return; // allow normal navigation
    e.preventDefault();
    setModalOpen(true);
  };

  const isMobile = variant === 'mobile';
  // sandbox navbar styling; active variant adds `nav-main active-nav`.
  const baseLink = isMobile
    ? 'block py-3 nav-link text-right text-lg font-medium text-black no-underline'
    : 'nav-link px-4 text-lg font-medium text-black no-underline';
  const activeLink = isMobile
    ? 'block py-3 nav-link nav-main active-nav text-right text-lg font-medium no-underline'
    : 'nav-link nav-main active-nav px-4 text-lg font-medium no-underline';
  const liClass = isMobile ? '' : 'nav-item list-none';

  // ag-local hotels-route logic: exact-or-segment match so siblings like /manage-bookings-archive
  // don't false-positive against the My Bookings nav.
  const matches = (prefix: string) => pathname === prefix || pathname.startsWith(prefix + '/');
  // My Bookings links to the CLEAN URL `/manage-bookings` (the next.config.js rewrite maps it onto the
  // real `/hotels/manage-bookings` route on the hotels deploy). Active-highlight must match BOTH
  // the clean URL (what the browser shows after a rewrite) and the underlying `/hotels/*` route
  // (direct/SSR navigation).
  const myBookingsHref = '/manage-bookings';
  const isSports = pathname === '/' || matches('/sports');
  const isConcerts = matches('/concerts');
  const isMyBookings = matches('/manage-bookings') || matches('/hotels/manage-bookings');
  // Don't double-highlight Hotels + My Bookings when on the hotels manage-bookings route.
  const isHotels = matches('/hotels') && !isMyBookings;

  return (
    <>
      <li className={liClass}>
        <Link href="/sports" className={isSports ? activeLink : baseLink}>
          Sports
        </Link>
      </li>
      <li className={liClass}>
        <Link href="/shows" className={baseLink}>
          Shows
        </Link>
      </li>
      <li className={liClass}>
        <Link href="/hotels" className={isHotels ? activeLink : baseLink}>
          Hotels
        </Link>
      </li>
      <li className={liClass}>
        <Link href="/concerts" className={isConcerts ? activeLink : baseLink}>
          Concerts
        </Link>
      </li>
     {/* <li className={liClass}>
        <a href="/restaurants" className={isRestaurants ? (isMobile ? 'block py-3 nav-link text-right text-lg font-medium no-underline active-nav' : 'nav-link nav-main active-nav px-4 text-lg font-medium no-underline') : baseLink}>
          Restaurants
        </a>
      </li> */}

      <li className={liClass}>
        <Link
          href={myBookingsHref}
          className={isMyBookings ? activeLink : baseLink}
          onClick={handleMyBookings}
        >
          My Bookings
        </Link>
      </li>
      <LoginModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onReopen={() => setModalOpen(true)}
      />
    </>
  );
}
